import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { AlertTriangle, Plus, CreditCard, CheckCircle, Printer, Eye, Search } from 'lucide-react';
import axios from 'axios';
import { formatDate } from '../../utils/dateTimeUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    'Authorization': token ? `Bearer ${token}` : ''
  };
};

const DamagedBooks = () => {
  const [damagedBooks, setDamagedBooks] = useState([]);
  const [books, setBooks] = useState([]);
  const [members, setMembers] = useState([]);
  const [banks, setBanks] = useState([]);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isPayModalOpen, setIsPayModalOpen] = useState(false);
  const [selectedDamage, setSelectedDamage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [damageFine, setDamageFine] = useState(100);

  // Accession number search state
  const [accessionSearch, setAccessionSearch] = useState('');
  const [selectedBook, setSelectedBook] = useState(null);
  const [selectedCopy, setSelectedCopy] = useState(null);
  const [availableCopies, setAvailableCopies] = useState([]);
  const [bookSearchTerm, setBookSearchTerm] = useState('');

  const [formData, setFormData] = useState({
    book_id: '',
    book_title: '',
    copy_id: '',
    accession_number: '',
    member_id: '',
    member_name: '',
    damage_type: 'Torn',
    damage_description: '',
    penalty_amount: 100,
    reported_by: ''
  });
  const [paymentData, setPaymentData] = useState({
    payment_mode: 'Cash',
    amount: 0,
    bank_id: '',
    bank_name: ''
  });
  const [isReceiptModalOpen, setIsReceiptModalOpen] = useState(false);
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [institutionInfo, setInstitutionInfo] = useState(null);
  const receiptRef = useRef(null);

  useEffect(() => {
    fetchDamagedBooks();
    fetchBooks();
    fetchMembers();
    fetchDamageFine();
    fetchBanks();
    fetchInstitutionInfo();
  }, []);

  const fetchInstitutionInfo = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/tenant/info`, { headers: getAuthHeaders() });
      setInstitutionInfo(response.data);
    } catch (error) {
      console.error('Error fetching institution info:', error);
    }
  };

  const fetchDamagedBooks = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/library/damaged-books`, { headers: getAuthHeaders() });
      setDamagedBooks(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching damaged books:', error);
    }
  };

  const fetchBooks = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/library/books?limit=10000`, { headers: getAuthHeaders() });
      if (Array.isArray(response.data)) {
        setBooks(response.data);
      } else if (response.data?.data) {
        setBooks(Array.isArray(response.data.data) ? response.data.data : []);
      } else {
        setBooks([]);
      }
    } catch (error) {
      console.error('Error fetching books:', error);
    }
  };

  const fetchMembers = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/library/members`, { headers: getAuthHeaders() });
      setMembers(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching members:', error);
    }
  };

  const fetchDamageFine = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/master-settings/fine-settings`, { headers: getAuthHeaders() });
      const fines = Array.isArray(response.data) ? response.data : [];
      const fine = fines.find(f => f.fine_type === 'Library Damage Fine' && f.is_active);
      if (fine) {
        setDamageFine(fine.amount);
        setFormData(prev => ({ ...prev, penalty_amount: fine.amount }));
      }
    } catch (error) {
      console.error('Error fetching fine settings:', error);
    }
  };

  const fetchBanks = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/master-settings/banks`, { headers: getAuthHeaders() });
      setBanks(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching banks:', error);
      setBanks([]);
    }
  };

  // Fetch copies for a specific book (only Available/Issued statuses)
  const fetchBookCopies = async (bookId) => {
    try {
      const response = await axios.get(`${API_URL}/api/library/books/${bookId}/copies`, { headers: getAuthHeaders() });
      const copies = Array.isArray(response.data) ? response.data : [];
      // For damage: show Available copies (can be damaged directly) 
      const damageable = copies.filter(c => c.status === 'Available');
      setAvailableCopies(damageable);
      if (damageable.length > 0) {
        handleCopySelect(damageable[0]);
      } else {
        setSelectedCopy(null);
      }
    } catch (error) {
      console.error('Error fetching copies:', error);
      setAvailableCopies([]);
    }
  };

  // Search by accession number
  const handleAccessionSearch = async () => {
    if (!accessionSearch.trim()) return;
    try {
      const response = await axios.get(`${API_URL}/api/library/accession-register?search=${accessionSearch.trim()}`, { headers: getAuthHeaders() });
      const data = response.data?.copies || [];
      const match = data.find(c =>
        c.accession_number?.toLowerCase() === accessionSearch.trim().toLowerCase()
      );
      if (match) {
        if (match.status === 'Damaged') {
          alert(`Copy ${accessionSearch} is already marked as "Damaged"`);
          return;
        }
        if (match.status === 'Issued') {
          alert(`Copy ${accessionSearch} is currently "Issued". Return it first before marking as damaged.`);
          return;
        }
        const book = books.find(b => b.id === match.book_id);
        if (book) {
          setSelectedBook(book);
          const copyObj = { id: match.id, accession_number: match.accession_number, copy_number: match.copy_number, status: match.status };
          setSelectedCopy(copyObj);
          setAvailableCopies([copyObj]);
          setFormData(prev => ({
            ...prev,
            book_id: book.id,
            book_title: book.title,
            copy_id: match.id,
            accession_number: match.accession_number
          }));
          setBookSearchTerm('');
        } else {
          alert('Book found but not loaded in the system.');
        }
      } else {
        alert(`No copy found with accession number: ${accessionSearch}`);
      }
    } catch (error) {
      alert('Error searching accession number');
    }
  };

  // Select a book from the book list
  const handleBookSelect = (bookId) => {
    const book = books.find(b => b.id === bookId);
    if (!book) return;
    setSelectedBook(book);
    setSelectedCopy(null);
    setAvailableCopies([]);
    setAccessionSearch('');
    setFormData(prev => ({
      ...prev,
      book_id: bookId,
      book_title: book.title,
      copy_id: '',
      accession_number: ''
    }));
    fetchBookCopies(bookId);
  };

  // Select a specific copy
  const handleCopySelect = (copy) => {
    setSelectedCopy(copy);
    setFormData(prev => ({
      ...prev,
      copy_id: copy.id,
      accession_number: copy.accession_number
    }));
  };

  const handleMemberSelect = (e) => {
    const memberId = e.target.value;
    const member = members.find(m => m.id === memberId);
    setFormData(prev => ({
      ...prev,
      member_id: memberId,
      member_name: member ? member.user_name : ''
    }));
  };

  const resetAddModal = () => {
    setSelectedBook(null);
    setSelectedCopy(null);
    setAvailableCopies([]);
    setAccessionSearch('');
    setBookSearchTerm('');
    setFormData({
      book_id: '',
      book_title: '',
      copy_id: '',
      accession_number: '',
      member_id: '',
      member_name: '',
      damage_type: 'Torn',
      damage_description: '',
      penalty_amount: damageFine,
      reported_by: ''
    });
  };

  const handleAddDamage = async (e) => {
    e.preventDefault();
    if (!formData.copy_id && !formData.accession_number) {
      alert('Please select a specific copy (accession number) to mark as damaged');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/library/damaged-books`, formData, { headers: getAuthHeaders() });
      alert('Damaged book reported successfully!');
      fetchDamagedBooks();
      fetchBooks();
      setIsAddModalOpen(false);
      resetAddModal();
    } catch (error) {
      console.error('Error reporting damage:', error);
      alert(error.response?.data?.detail || 'Failed to report damage');
    } finally {
      setLoading(false);
    }
  };

  const handlePayClick = (damage) => {
    setSelectedDamage(damage);
    setPaymentData({
      payment_mode: 'Cash',
      amount: damage.penalty_amount,
      bank_id: '',
      bank_name: ''
    });
    setIsPayModalOpen(true);
  };

  const handleBankSelect = (e) => {
    const bankId = e.target.value;
    const bank = banks.find(b => b.id === bankId);
    setPaymentData({ ...paymentData, bank_id: bankId, bank_name: bank ? bank.bank_name : '' });
  };

  const handlePayment = async (e) => {
    e.preventDefault();
    if (paymentData.payment_mode !== 'Cash' && !paymentData.bank_id) {
      alert('Please select a bank for non-cash payment');
      return;
    }
    setLoading(true);
    try {
      let url = `${API_URL}/api/library/damaged-books/${selectedDamage.id}/pay?payment_mode=${paymentData.payment_mode}&amount=${paymentData.amount}`;
      if (paymentData.bank_id) {
        url += `&bank_id=${paymentData.bank_id}&bank_name=${encodeURIComponent(paymentData.bank_name)}`;
      }
      await axios.put(url, {}, { headers: getAuthHeaders() });
      alert(`Payment of Rs.${paymentData.amount} collected successfully via ${paymentData.payment_mode}${paymentData.bank_name ? ` (${paymentData.bank_name})` : ''}!`);
      fetchDamagedBooks();
      setIsPayModalOpen(false);
      setSelectedDamage(null);
    } catch (error) {
      console.error('Error processing payment:', error);
      alert(error.response?.data?.detail || 'Failed to process payment');
    } finally {
      setLoading(false);
    }
  };

  // Bug fix: removed shadowing formatDate – using imported one

  const pendingPayments = damagedBooks.filter(d => !d.penalty_paid);
  const completedPayments = damagedBooks.filter(d => d.penalty_paid);

  const handleViewReceipt = (damage) => {
    setSelectedReceipt(damage);
    setIsReceiptModalOpen(true);
  };

  const handlePrintReceipt = () => {
    const printContent = receiptRef.current;
    const printWindow = window.open('', '', 'width=800,height=600');
    printWindow.document.write(`
      <html>
        <head>
          <title>Library Damage Fine Receipt</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            .receipt { max-width: 400px; margin: 0 auto; border: 2px solid #000; padding: 20px; }
            .header { text-align: center; border-bottom: 2px solid #000; padding-bottom: 10px; margin-bottom: 15px; }
            .header h2 { margin: 0; font-size: 18px; }
            .header p { margin: 5px 0; font-size: 12px; }
            .title { text-align: center; font-weight: bold; margin: 15px 0; font-size: 16px; background: #f0f0f0; padding: 8px; }
            .row { display: flex; justify-content: space-between; margin: 8px 0; font-size: 14px; }
            .row .label { font-weight: bold; }
            .amount { font-size: 18px; font-weight: bold; text-align: center; margin: 15px 0; padding: 10px; background: #e8f5e9; border-radius: 5px; }
            .footer { text-align: center; margin-top: 20px; font-size: 11px; border-top: 1px dashed #000; padding-top: 10px; }
            @media print { body { print-color-adjust: exact; } }
          </style>
        </head>
        <body>${printContent.innerHTML}</body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const filteredBooks = books.filter(book =>
    book.title?.toLowerCase().includes(bookSearchTerm.toLowerCase()) ||
    book.author_name?.toLowerCase().includes(bookSearchTerm.toLowerCase()) ||
    book.isbn?.toLowerCase().includes(bookSearchTerm.toLowerCase())
  );

  return (
    <div className="p-6 space-y-6">
      {/* Damage Fine Info */}
      <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <AlertTriangle className="w-5 h-5 text-orange-600" />
          <div>
            <span className="font-medium text-orange-800">Library Damage Fine: </span>
            <span className="text-orange-700">Rs.{damageFine} (Fixed)</span>
            <span className="text-orange-600 text-sm ml-2">(Master Settings - Fine Setting)</span>
          </div>
        </div>
        <Button data-testid="report-damage-btn" onClick={() => { resetAddModal(); setIsAddModalOpen(true); }} className="gap-2">
          <Plus className="w-4 h-4" /> Report Damage
        </Button>
      </div>

      {/* Pending Payments */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-red-600">
            <AlertTriangle className="w-5 h-5" />
            Pending Damage Payments ({pendingPayments.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {pendingPayments.length === 0 ? (
            <p className="text-gray-500 text-center py-4">No pending damage payments</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b bg-red-50">
                    <th className="text-left p-3">Accession No.</th>
                    <th className="text-left p-3">Book</th>
                    <th className="text-left p-3">Member</th>
                    <th className="text-left p-3">Damage Type</th>
                    <th className="text-left p-3">Date</th>
                    <th className="text-left p-3">Fine Amount</th>
                    <th className="text-left p-3">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {pendingPayments.map((damage) => (
                    <tr key={damage.id} className="border-b hover:bg-gray-50" data-testid={`pending-damage-${damage.id}`}>
                      <td className="p-3">
                        <span className="font-mono text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                          {damage.accession_number || 'N/A'}
                        </span>
                      </td>
                      <td className="p-3 font-medium">{damage.book_title}</td>
                      <td className="p-3">{damage.member_name || 'N/A'}</td>
                      <td className="p-3">
                        <span className="px-2 py-1 bg-red-100 text-red-700 rounded text-sm">
                          {damage.damage_type}
                        </span>
                      </td>
                      <td className="p-3">{formatDate(damage.damage_date)}</td>
                      <td className="p-3 font-bold text-red-600">Rs.{damage.penalty_amount}</td>
                      <td className="p-3">
                        <Button size="sm" data-testid={`collect-fine-${damage.id}`} onClick={() => handlePayClick(damage)} className="gap-1">
                          <CreditCard className="w-3 h-3" /> Collect Fine
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Completed Payments */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-green-600">
            <CheckCircle className="w-5 h-5" />
            Paid Damage Fines ({completedPayments.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {completedPayments.length === 0 ? (
            <p className="text-gray-500 text-center py-4">No completed payments</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b bg-green-50">
                    <th className="text-left p-3">Receipt No</th>
                    <th className="text-left p-3">Accession No.</th>
                    <th className="text-left p-3">Book</th>
                    <th className="text-left p-3">Member</th>
                    <th className="text-left p-3">Damage Type</th>
                    <th className="text-left p-3">Payment Date</th>
                    <th className="text-left p-3">Amount</th>
                    <th className="text-left p-3">Mode</th>
                    <th className="text-left p-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {completedPayments.map((damage) => (
                    <tr key={damage.id} className="border-b hover:bg-gray-50">
                      <td className="p-3 font-mono text-sm text-blue-600">
                        {damage.receipt_no || `LIBDMG-${damage.id?.slice(0,8).toUpperCase()}`}
                      </td>
                      <td className="p-3">
                        <span className="font-mono text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                          {damage.accession_number || 'N/A'}
                        </span>
                      </td>
                      <td className="p-3 font-medium">{damage.book_title}</td>
                      <td className="p-3">{damage.member_name || 'N/A'}</td>
                      <td className="p-3">
                        <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded text-sm">
                          {damage.damage_type}
                        </span>
                      </td>
                      <td className="p-3">{formatDate(damage.payment_date)}</td>
                      <td className="p-3 font-bold text-green-600">Rs.{damage.penalty_amount}</td>
                      <td className="p-3">
                        <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-sm">
                          {damage.payment_mode || 'Cash'}
                        </span>
                      </td>
                      <td className="p-3">
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" onClick={() => handleViewReceipt(damage)} className="gap-1">
                            <Eye className="w-3 h-3" /> View
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => { setSelectedReceipt(damage); setTimeout(handlePrintReceipt, 100); }} className="gap-1">
                            <Printer className="w-3 h-3" /> Print
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Damage Modal - Accession-Number Centric */}
      <Dialog open={isAddModalOpen} onOpenChange={setIsAddModalOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Report Damaged Book</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddDamage} className="space-y-4">
            {/* Method 1: Search by Accession Number */}
            <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
              <Label className="text-blue-800 font-medium">Search by Accession Number</Label>
              <div className="flex gap-2 mt-2">
                <Input
                  data-testid="accession-search-input"
                  placeholder="Enter accession number (e.g., ACC-2026-00001)"
                  value={accessionSearch}
                  onChange={(e) => setAccessionSearch(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAccessionSearch(); }}}
                />
                <Button type="button" data-testid="accession-search-btn" onClick={handleAccessionSearch} variant="outline">
                  <Search className="w-4 h-4" />
                </Button>
              </div>
            </div>

            <div className="text-center text-sm text-gray-400">- OR -</div>

            {/* Method 2: Select Book then Copy */}
            <div>
              <Label>Select Book</Label>
              <select
                data-testid="book-select"
                value={formData.book_id}
                onChange={(e) => handleBookSelect(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              >
                <option value="">-- Select Book --</option>
                {books.map((book) => (
                  <option key={book.id} value={book.id}>
                    {book.title} ({book.available_copies} available)
                  </option>
                ))}
              </select>
            </div>

            {/* Show available copies for the selected book */}
            {selectedBook && availableCopies.length > 0 && (
              <div>
                <Label>Select Copy (Accession Number) *</Label>
                <div className="space-y-1 max-h-40 overflow-y-auto border rounded-lg p-2">
                  {availableCopies.map((copy) => (
                    <label
                      key={copy.id}
                      className={`flex items-center gap-3 p-2 rounded cursor-pointer hover:bg-gray-50 ${selectedCopy?.id === copy.id ? 'bg-blue-50 border border-blue-300' : 'border border-transparent'}`}
                    >
                      <input
                        type="radio"
                        name="copy_select"
                        checked={selectedCopy?.id === copy.id}
                        onChange={() => handleCopySelect(copy)}
                        className="w-4 h-4"
                      />
                      <span className="font-mono text-sm bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                        {copy.accession_number}
                      </span>
                      <span className="text-sm text-gray-500">Copy #{copy.copy_number}</span>
                      <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded ml-auto">
                        {copy.status}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {selectedBook && availableCopies.length === 0 && (
              <div className="bg-yellow-50 border border-yellow-200 p-3 rounded-lg text-yellow-800 text-sm">
                No available copies found for this book. All copies may be issued or already damaged.
              </div>
            )}

            {/* Selected Copy Info */}
            {selectedCopy && selectedBook && (
              <div className="bg-green-50 border border-green-200 p-3 rounded-lg">
                <p className="text-sm text-green-800">
                  <strong>Selected:</strong> {selectedBook.title} - <span className="font-mono">{selectedCopy.accession_number}</span> (Copy #{selectedCopy.copy_number})
                </p>
              </div>
            )}

            <div>
              <Label>Member (if applicable)</Label>
              <select
                data-testid="member-select"
                value={formData.member_id}
                onChange={handleMemberSelect}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              >
                <option value="">-- Select Member --</option>
                {members.map((member) => (
                  <option key={member.id} value={member.id}>{member.user_name}</option>
                ))}
              </select>
            </div>

            <div>
              <Label>Damage Type *</Label>
              <select
                required
                data-testid="damage-type-select"
                value={formData.damage_type}
                onChange={(e) => setFormData({ ...formData, damage_type: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              >
                <option value="Torn">Torn Pages</option>
                <option value="Water Damage">Water Damage</option>
                <option value="Lost">Lost</option>
                <option value="Cover Damage">Cover Damage</option>
                <option value="Writing/Marks">Writing/Marks</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div>
              <Label>Description *</Label>
              <textarea
                required
                data-testid="damage-description"
                value={formData.damage_description}
                onChange={(e) => setFormData({ ...formData, damage_description: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                rows={3}
                placeholder="Describe the damage..."
              />
            </div>

            <div>
              <Label>Penalty Amount (Rs.)</Label>
              <Input
                type="number"
                data-testid="penalty-amount"
                value={formData.penalty_amount}
                onChange={(e) => setFormData({ ...formData, penalty_amount: parseFloat(e.target.value) || 0 })}
              />
              <p className="text-xs text-gray-500 mt-1">Default from Fine Setting: Rs.{damageFine}</p>
            </div>

            <div>
              <Label>Reported By *</Label>
              <Input
                required
                data-testid="reported-by"
                value={formData.reported_by}
                onChange={(e) => setFormData({ ...formData, reported_by: e.target.value })}
                placeholder="Staff name"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsAddModalOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="submit-damage-btn" disabled={loading || (!formData.copy_id && !formData.accession_number)}>
                {loading ? 'Saving...' : 'Report Damage'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Payment Modal */}
      <Dialog open={isPayModalOpen} onOpenChange={setIsPayModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Collect Damage Fine</DialogTitle>
          </DialogHeader>
          {selectedDamage && (
            <form onSubmit={handlePayment} className="space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                <p><strong>Book:</strong> {selectedDamage.book_title}</p>
                {selectedDamage.accession_number && (
                  <p><strong>Accession No.:</strong> <span className="font-mono bg-blue-100 text-blue-700 px-2 py-0.5 rounded">{selectedDamage.accession_number}</span></p>
                )}
                <p><strong>Member:</strong> {selectedDamage.member_name || 'N/A'}</p>
                <p><strong>Damage:</strong> {selectedDamage.damage_type}</p>
                <p className="text-lg font-bold text-red-600">Fine: Rs.{selectedDamage.penalty_amount}</p>
              </div>

              <div>
                <Label>Payment Mode</Label>
                <select
                  value={paymentData.payment_mode}
                  onChange={(e) => setPaymentData({ ...paymentData, payment_mode: e.target.value, bank_id: '', bank_name: '' })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="Cash">Cash</option>
                  <option value="Bank">Bank Transfer</option>
                  <option value="UPI">UPI</option>
                  <option value="Card">Card</option>
                </select>
              </div>

              {paymentData.payment_mode !== 'Cash' && (
                <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
                  <Label className="text-blue-800">Select Bank *</Label>
                  <select
                    required
                    value={paymentData.bank_id}
                    onChange={handleBankSelect}
                    className="w-full mt-2 px-3 py-2 border border-blue-300 rounded-lg"
                  >
                    <option value="">-- Select Bank --</option>
                    {banks.map((bank) => (
                      <option key={bank.id} value={bank.id}>
                        {bank.bank_name} (Balance: Rs.{bank.current_balance?.toLocaleString('en-IN') || 0})
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div>
                <Label>Amount (Rs.)</Label>
                <Input
                  type="number"
                  value={paymentData.amount}
                  onChange={(e) => setPaymentData({ ...paymentData, amount: parseFloat(e.target.value) || 0 })}
                />
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsPayModalOpen(false)}>Cancel</Button>
                <Button type="submit" disabled={loading}>
                  {loading ? 'Processing...' : `Collect Rs.${paymentData.amount}`}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Receipt View Modal */}
      <Dialog open={isReceiptModalOpen} onOpenChange={setIsReceiptModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Library Damage Fine Receipt</DialogTitle>
          </DialogHeader>
          {selectedReceipt && (
            <div>
              <div ref={receiptRef}>
                <div className="receipt border-2 border-gray-800 p-5">
                  <div className="header text-center border-b-2 border-gray-800 pb-3 mb-4">
                    <h2 className="text-lg font-bold">{institutionInfo?.institution_name || 'Institution Name'}</h2>
                    <p className="text-sm text-gray-600">{institutionInfo?.address || ''}</p>
                    <p className="text-sm text-gray-600">{institutionInfo?.phone || ''}</p>
                  </div>
                  
                  <div className="title text-center font-bold bg-gray-100 py-2 mb-4">
                    LIBRARY DAMAGE FINE RECEIPT
                  </div>
                  
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="font-semibold">Receipt No:</span>
                      <span className="font-mono">{selectedReceipt.receipt_no || `LIBDMG-${selectedReceipt.id?.slice(0,8).toUpperCase()}`}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-semibold">Date:</span>
                      <span>{formatDate(selectedReceipt.payment_date)}</span>
                    </div>
                    {selectedReceipt.accession_number && (
                      <div className="flex justify-between">
                        <span className="font-semibold">Accession No.:</span>
                        <span className="font-mono">{selectedReceipt.accession_number}</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="font-semibold">Member:</span>
                      <span>{selectedReceipt.member_name || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-semibold">Book:</span>
                      <span>{selectedReceipt.book_title}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-semibold">Damage Type:</span>
                      <span className="text-orange-600">{selectedReceipt.damage_type}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-semibold">Payment Mode:</span>
                      <span>{selectedReceipt.payment_mode || 'Cash'}</span>
                    </div>
                  </div>
                  
                  <div className="amount text-center bg-green-100 py-3 my-4 rounded">
                    <span className="text-lg font-bold text-green-700">Amount Paid: Rs.{selectedReceipt.penalty_amount}</span>
                  </div>
                  
                  <div className="footer text-center border-t border-dashed border-gray-400 pt-3 text-xs text-gray-500">
                    <p>Thank you for your payment</p>
                    <p>This is a computer-generated receipt</p>
                  </div>
                </div>
              </div>
              
              <DialogFooter className="mt-4">
                <Button variant="outline" onClick={() => setIsReceiptModalOpen(false)}>Close</Button>
                <Button onClick={handlePrintReceipt} className="gap-2">
                  <Printer className="w-4 h-4" /> Print Receipt
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DamagedBooks;
